/**
 * Utility functions for nomination approval error handling
 * Separate from self-appraisal to avoid conflicts
 */

/**
 * Extract user-friendly error message from API response
 * @param error - The error object from API call
 * @returns User-friendly error message string
 */
export const getNominationErrorMessage = (error: unknown): string => {
  console.log('🔍 Processing error in getNominationErrorMessage:', error);
  
  // Handle API response errors
  if (error && typeof error === "object" && "response" in error) {
    const apiError = error as { 
      response?: { 
        data?: { 
          message?: string; 
          error?: string | { error?: string };
          status?: boolean;
          status_code?: number;
          data?: {
            failed_items?: Array<{ nomination_id: number; error: string }>;
          };
        }; 
        message?: string; 
        status?: number;
      } 
    };
    
    console.log('🔍 API Error response data:', apiError.response?.data);
    
    // Handle the specific API error format you showed:
    // { "status": false, "message": "Nomination is not in a pending state", "data": {}, "error": { "error": "Invalid nomination state" }, "status_code": 409 }
    
    // First priority: API response message
    if (apiError.response?.data?.message) {
      return apiError.response.data.message;
    }
    
    // Handle bulk API failed items
    if (apiError.response?.data?.data?.failed_items && Array.isArray(apiError.response.data.data.failed_items)) {
      const failedErrors = apiError.response.data.data.failed_items
        .map(item => item.error)
        .filter(Boolean)
        .join(', ');
      if (failedErrors) {
        return failedErrors;
      }
    }
    
    // Second priority: nested error message
    if (apiError.response?.data?.error) {
      if (typeof apiError.response.data.error === 'string') {
        return apiError.response.data.error;
      } else if (apiError.response.data.error?.error) {
        return apiError.response.data.error.error;
      }
    }
    
    // Third priority: response level message
    if (apiError.response?.message) {
      return apiError.response.message;
    }
    
    // Handle specific HTTP status codes
    if (apiError.response?.status || apiError.response?.data?.status_code) {
      const statusCode = apiError.response?.status || apiError.response?.data?.status_code;
      switch (statusCode) {
        case 401:
          return 'Authentication required. Please log in again.';
        case 403:
          return 'You do not have permission to perform this action.';
        case 404:
          return 'The requested nomination was not found.';
        case 409:
          return 'Conflict: The nomination may not be in the correct state for this action.';
        case 422:
          return 'Invalid data provided. Please check your input.';
        case 500:
          return 'Server error occurred. Please try again later.';
        default:
          return 'An error occurred while processing your request.';
      }
    }
  }
  
  // Handle direct API response format (when error is the response itself)
  // Matches both objects that have a "message" key and objects that only have an "error" key
  if (error && typeof error === "object" && ("message" in error || "error" in error)) {
    const directError = error as { 
      message?: string; 
      error?: string | { error?: string };
      status?: boolean;
      status_code?: number;
      data?: {
        failed_items?: Array<{ nomination_id: number; error: string }>;
      };
    };
    
    console.log('🔍 Direct error object:', directError);
    
    if (directError.message) {
      return directError.message;
    }
    
    // Handle bulk API failed items in direct error
    if (directError.data?.failed_items && Array.isArray(directError.data.failed_items)) {
      const failedErrors = directError.data.failed_items
        .map(item => item.error)
        .filter(Boolean)
        .join(', ');
      if (failedErrors) {
        return failedErrors;
      }
    }
    
    if (directError.error) {
      if (typeof directError.error === 'string') {
        return directError.error;
      } else if (directError.error?.error) {
        return directError.error.error;
      }
    }
  }
  
  // Handle Error objects
  if (error instanceof Error) {
    return error.message;
  }
  
  // Handle string errors
  if (typeof error === "string") {
    return error;
  }
  
  console.log('🔍 Fallback error message for:', error);
  // Default fallback
  return "An unexpected error occurred. Please try again.";
};

/**
 * Extract success message from API response
 * @param response - The API response object
 * @returns Success message string
 */
export const getNominationSuccessMessage = (response: any): string => {
  // Handle bulk API responses
  if (response?.data?.success_count !== undefined) {
    const { success_count } = response.data;
    if (success_count > 0) {
      return `${success_count} nomination${success_count > 1 ? 's' : ''} processed successfully.`;
    }
  }
  
  // Handle regular API responses
  if (response?.message) {
    return response.message;
  }
  
  if (response?.data?.message) {
    return response.data.message;
  }
  
  return "Operation completed successfully.";
};
